import React, { useState, useRef } from 'react';
import {
  Box, TextField, Typography, IconButton, Tooltip, CircularProgress, Alert, Button,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';

/**
 * Campo de documento: se arrastra un fichero, se elige del disco o se pega una URL.
 *
 * Con `onUpload` el fichero se archiva ademas en el servidor y se guarda el
 * enlace que devuelva. Si no se puede archivar, se guarda igualmente lo que se
 * haya podido sacar del arrastre: el campo nunca se queda vacio por un fallo de
 * la subida.
 */
export default function FileDropField({ value, onChange, label, disabled, onUpload }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);   // {severity, text}
  const inputRef = useRef(null);

  const extractPath = (dataTransfer) => {
    // 1. Try text/uri-list (e.g. file dragged from file manager or browser)
    const uriList = dataTransfer.getData('text/uri-list');
    if (uriList) return uriList.split('\n')[0].trim();

    // 2. Try text/plain (path or URL pasted / dragged)
    const plain = dataTransfer.getData('text/plain');
    if (plain) return plain.trim();

    // 3. Fallback: use the File name (local file drag gives name only in browsers)
    const file = dataTransfer.files?.[0];
    if (file) return file.name;

    return '';
  };

  const handleFile = async (file, fallback = '') => {
    if (!onUpload) {
      onChange(fallback || file.name);
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const res = await onUpload(file);
      // Se guarda el enlace del archivo si se ha podido archivar y, si no, lo que
      // se haya podido sacar del arrastre: la casilla nunca se queda vacia.
      onChange(res?.url || fallback || file.name);
      setNotice({
        severity: res?.stored ? (res.already_existed ? 'info' : 'success') : 'warning',
        text: res?.message || '',
      });
    } catch (err) {
      onChange(fallback || file.name);
      setNotice({
        severity: 'warning',
        text: err.response?.data?.detail
          || 'No se ha podido archivar el fichero. Se guarda solo la referencia.',
      });
    } finally {
      setBusy(false);
    }
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    const path = extractPath(e.dataTransfer);
    if (file) {
      // El navegador no da la ruta real del fichero, asi que el arrastre solo
      // sirve de verdad si el contenido se sube.
      handleFile(file, path && path !== file.name ? path : '');
    } else if (path) {
      onChange(path);
    }
  };

  const displayName = value
    ? decodeURIComponent(value.replace(/\\/g, '/').split('/').pop().split('?')[0]) || value
    : '';

  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        border: dragging ? '2px dashed #c62828' : '2px dashed transparent',
        borderRadius: 1,
        transition: 'border-color 0.2s',
        backgroundColor: dragging ? 'rgba(198,40,40,0.04)' : 'transparent',
        p: 0.5,
      }}
    >
      {busy ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">Archivando el documento...</Typography>
        </Box>
      ) : value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachFileIcon fontSize="small" color="primary" />
          <Typography
            variant="body2"
            component="a"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', maxWidth: 350, cursor: 'pointer',
              color: 'primary.main', textDecoration: 'underline',
            }}
            title={value}
          >
            {displayName}
          </Typography>
          <Tooltip title="Abrir en nueva ventana">
            <IconButton
              size="small"
              component="a"
              href={value}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Quitar enlace">
            <IconButton size="small" onClick={() => { onChange(''); setNotice(null); }} disabled={disabled}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            label={label || 'URL del documento'}
            placeholder="Arrastra un fichero aqui o pega una URL"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {onUpload && (
            <>
              <Button
                size="small" startIcon={<UploadFileIcon />} disabled={disabled}
                onClick={() => inputRef.current?.click()} sx={{ whiteSpace: 'nowrap' }}
              >
                Elegir
              </Button>
              <input
                ref={inputRef} type="file" hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = '';
                }}
              />
            </>
          )}
        </Box>
      )}

      {notice && notice.text && (
        <Alert
          severity={notice.severity}
          sx={{ mt: 1, py: 0 }}
          onClose={() => setNotice(null)}
        >
          {notice.text}
        </Alert>
      )}
    </Box>
  );
}
